/**
 * DIEUKYDIEU.TV - APPLICATION MAIN ENTRYPOINT (ES6 MODULE)
 * Central coordinator orchestrating all modules and exposing backward-compatible global hooks.
 */

// Import Modules
import { state, getState, setState } from './state.js';
import { dict, t, applyLanguage, toggleLangMenu, selectLang } from './i18n.js';
import { initTheme, applyTheme, toggleTheme } from './theme.js';
import { initHeaderAndScrollListeners, scrollToTop, toggleDesktopSidebar, toggleMobileSidebar, closeMobileSidebar, showToast, initMaterial3Ripple, enableSmoothHorizontalInteraction } from './ui.js';
import { CAROUSEL_TOPICS, renderCarousel, updateCarousel, exploreFromTopic, initCarouselControls } from './carousel.js';
import { openTopicQuiz, closeTopicQuiz, TOPIC_QUIZZES } from './topic-quiz.js';
import { openWebStory, closeWebStory, nextWebStorySlide, prevWebStorySlide } from './web-story.js';
import * as API from './api/index.js';
import { renderExploreSpotlight } from './spotlight.js';
import { goToMasterBanner, nextMasterBanner, prevMasterBanner } from './modules/banner-slider.js';
import { initChipScroll, refreshAllChipScrolls } from './chip-scroll.js';
import { FALLBACK_SITEMAP_DATA, getSectionsData, getSitemapTree, loadExploreDataAsync, renderExploreSections, openCategoryBySlug, closeCategoryDetail, renderTier1Chips, onSelectTier1, renderTier2Chips, expandTier2Accordion, collapseTier2Accordion, onSelectTier2, applyCategoryFilters, updateFilterStatusBar, renderCategoryArticlesGrid, updatePaginationAndInfiniteScroll, loadMoreCategoryArticles, goToCategoryPage, setupCategoryInfiniteScroll } from './explore.js';
import { navMap, updateNavigationIndicator, navigateToView, switchNav, handleHashRoute, handleSafeBack, openStoryDetail, openStoryBySlugOrId, goBackFromStory, openTruthArticle, initRouter, updateUniversalFloatingBar, handleUniversalFloatingBack, handleUniversalShare } from './router.js';
import { initStoryDetailModule, handleStoryLikeToggle, openStoryShare, changeStoryFontSize, resetStoryFontSize, shareStoryToChannel, copyStoryLink, openStoryQrCode, seeMoreCategoryStories, scrollToStoryTop, currentStoryArticle, toggleStoryAudioPlayback, copyStoryBookmarkQuote } from './story.js';
import { initReaderToolbar, toggleStoryReaderPopover, closeStoryReaderPopover, setStoryFontSizeStep, setStoryFontFamily, handleStoryAudioPlay, stopStorySpeech, updateReaderPopoverUI } from './reader-toolbar.js';
import { audioPlaylist, currentAudioIndex, isShuffle, repeatMode, currentPlaybackSpeed, favoriteTracks, activePlayerTab, getTrackTitle, getTrackArtist, updateCurrentTrackDisplay, updateSliderTrack, playAudio, togglePlay, updatePlayState, playNext, playPrev, seekRelative, toggleShuffle, toggleRepeat, toggleFavoriteCurrentTrack, updateFavoriteBtn, shareCurrentTrack, switchPlayerTab, toggleOptionsMenu, closeOptionsMenu, openSleepTimerFromMenu, renderQueueList, shufflePlaylist, cyclePlaybackSpeed, initVolumeControl, updateVolumeIcon, toggleMute, openSleepTimerModal, closeSleepTimerModal, setSleepTimer, formatTime, openFullPlayer, closeFullPlayer, closePlayer, initSeekbar, initAudioElementEvents, initKeyboardShortcuts, initPlayerSwipeGestures, toggleTimeDisplayMode, seekToLyricTime, toggleLyricsTab, toggleQueueTab, playNextBtn, setVolume } from './player.js';
import { SEARCH_HISTORY_KEY, DEFAULT_SEARCH_HISTORY, POPULAR_SEARCH_TOPICS, FEATURED_SUGGESTIONS, getUnifiedSearchDatabase, getSearchHistory, saveSearchHistory, addSearchHistory, removeSearchHistory, clearAllSearchHistory, removeVietnameseTones, highlightMatch, escapeHtml, escapeRegExp, filterSearchDatabase, createMediaRowHTML, focusDesktopSearch, handleDesktopSearchFocus, handleDesktopSearch, renderDesktopEmptyState, renderDesktopResults, clearDesktopSearch, closeDesktopSearchDropdown, applySearchQuery, viewAllSearchResults, handleSelectSearchResult, openMobileSearch, closeMobileSearch, clearMobileSearch, fillMobileSearch, handleMobileSearch, renderMobileEmptyState, renderMobileResults, renderQuickSearchResults, initSearchEvents } from './search.js';
import { getLikedList, checkIsLiked, getArticleLikes, toggleLikeArticle, openShareAction, openShareModalDesktop, closeShareModal, copyCurrentShareLink, shareToPlatform, generateQRCode, renderShareChannels } from './social.js';
import { initMediaView, renderMedia, renderMediaTier1Chips, filterMedia, filterMediaSubCategory, openVideoCinema, closeVideoCinema, openMediaLightbox, closeMediaLightbox, navigateMediaLightbox } from './media.js';
import { toggleLiveChat, sendQuickChatMessage, sendUserChatMessage, renderLiveChatSuggestions } from './livechat.js';
import { renderSidebarDynamicMenus, toggleSidebarGroup, navigateToSidebarGroup, toggleCategoryTree, initSidebarAccordion, updateSidebarFavoritesBadge, openFavoritesView, openHistoryView, openFeaturedArticles, recordReadingHistory, updateSidebarActive } from './sidebar.js';

import { registerWindowGlobals } from './modules/window-globals.js';

registerWindowGlobals({
    state, t, applyLanguage, toggleLangMenu, selectLang,
    initTheme, applyTheme, toggleTheme,
    scrollToTop, toggleDesktopSidebar, toggleMobileSidebar, closeMobileSidebar, showToast,
    enableSmoothHorizontalInteraction, initChipScroll, refreshAllChipScrolls,
    CAROUSEL_TOPICS, renderCarousel, updateCarousel, exploreFromTopic, openTopicQuiz, closeTopicQuiz, TOPIC_QUIZZES,
    openWebStory, closeWebStory, nextWebStorySlide, prevWebStorySlide, API,
    goToMasterBanner, nextMasterBanner, prevMasterBanner,
    renderExploreSpotlight, renderExploreSections, loadExploreDataAsync, openCategoryBySlug, closeCategoryDetail,
    renderTier1Chips, onSelectTier1, renderTier2Chips, onSelectTier2, expandTier2Accordion, collapseTier2Accordion,
    applyCategoryFilters, updateFilterStatusBar, renderCategoryArticlesGrid, loadMoreCategoryArticles, goToCategoryPage,
    navigateToView, switchNav, handleSafeBack, openStoryDetail, openStoryBySlugOrId, goBackFromStory, openTruthArticle,
    updateUniversalFloatingBar, handleUniversalFloatingBack, handleUniversalShare,
    handleStoryLikeToggle, openStoryShare, changeStoryFontSize, resetStoryFontSize, shareStoryToChannel, copyStoryLink, openStoryQrCode,
    seeMoreCategoryStories, scrollToStoryTop, toggleStoryAudioPlayback, copyStoryBookmarkQuote,
    toggleStoryReaderPopover, closeStoryReaderPopover, setStoryFontSizeStep, setStoryFontFamily, handleStoryAudioPlay, stopStorySpeech, updateReaderPopoverUI,
    audioPlaylist, playAudio, togglePlay, playNext, playPrev, seekRelative, toggleShuffle, toggleRepeat, toggleFavoriteCurrentTrack,
    shareCurrentTrack, switchPlayerTab, toggleOptionsMenu, closeOptionsMenu, openSleepTimerFromMenu, renderQueueList, shufflePlaylist,
    cyclePlaybackSpeed, toggleMute, openSleepTimerModal, closeSleepTimerModal, setSleepTimer, openFullPlayer, closeFullPlayer, closePlayer,
    toggleTimeDisplayMode, seekToLyricTime, toggleLyricsTab, toggleQueueTab, playNextBtn, setVolume,
    focusDesktopSearch, handleDesktopSearchFocus, handleDesktopSearch, renderDesktopResults, renderDesktopEmptyState, clearDesktopSearch, closeDesktopSearchDropdown,
    openMobileSearch, closeMobileSearch, clearMobileSearch, fillMobileSearch, handleMobileSearch, renderMobileEmptyState, renderMobileResults, renderQuickSearchResults,
    applySearchQuery, viewAllSearchResults, handleSelectSearchResult, removeSearchHistory, clearAllSearchHistory, getSearchHistory, saveSearchHistory, addSearchHistory,
    getLikedList, checkIsLiked, getArticleLikes, toggleLikeArticle, openShareAction, openShareModalDesktop, closeShareModal, copyCurrentShareLink, shareToPlatform,
    initMediaView, renderMedia, filterMedia, filterMediaSubCategory, openVideoCinema, closeVideoCinema, openMediaLightbox, closeMediaLightbox, navigateMediaLightbox,
    toggleLiveChat, sendQuickChatMessage, sendUserChatMessage,
    renderSidebarDynamicMenus, toggleSidebarGroup, navigateToSidebarGroup, toggleCategoryTree, initSidebarAccordion, updateSidebarFavoritesBadge,
    openFavoritesView, openHistoryView, openFeaturedArticles, recordReadingHistory, updateSidebarActive
});

/* ==========================================================================
   CROSS-MODULE EVENT BUS LISTENERS
   ========================================================================== */
window.addEventListener('dkd:langchange', () => {
    renderCarousel();
    renderExploreSections();
    renderMediaTier1Chips();
    renderMedia();
    renderSidebarDynamicMenus();
    renderLiveChatSuggestions();
    renderShareChannels();
    updateCurrentTrackDisplay();
    renderQueueList();

    const optSpeedSub = document.getElementById('fp-opt-speed-sub');
    if (optSpeedSub) {
        optSpeedSub.innerText = `${currentPlaybackSpeed}x ${currentPlaybackSpeed === 1.0 ? `(${t('player_speed_normal_word')})` : ''}`;
    }

    const desktopResults = document.getElementById('desktop-search-results');
    if (desktopResults && desktopResults.classList.contains('show')) {
        const deskInput = document.getElementById('desktop-search-input');
        if (deskInput && !deskInput.value.trim()) renderDesktopEmptyState(desktopResults);
    }
    const mobileModal = document.getElementById('mobile-search-modal');
    if (mobileModal && mobileModal.classList.contains('show')) {
        const mobileInput = document.getElementById('mobile-search-input');
        if (mobileInput && !mobileInput.value.trim()) renderMobileEmptyState();
    }
});

window.addEventListener('dkd:themechange', () => {
    const audioEl = document.getElementById('main-audio');
    if (audioEl && audioEl.duration) {
        const percent = (audioEl.currentTime / audioEl.duration) * 100;
        updateSliderTrack(percent);
    }
});

/* ==========================================================================
   APPLICATION BOOTSTRAP ON DOM READY
   ========================================================================== */
function initApp() {
    initMaterial3Ripple();
    initTheme();
    initHeaderAndScrollListeners();
    initRouter();
    initSeekbar();
    initAudioElementEvents();
    initVolumeControl();
    initPlayerSwipeGestures();
    initKeyboardShortcuts();
    initSearchEvents();

    const savedLang = localStorage.getItem('dieukydieu_lang') || 'vi';
    applyLanguage(savedLang);

    renderCarousel();
    renderExploreSections();
    initMediaView();
    renderLiveChatSuggestions();
    renderShareChannels();
    updateCurrentTrackDisplay();
    renderQueueList();
    initStoryDetailModule();
    initReaderToolbar();
    initSidebarAccordion();

    // Initial Route Handling
    handleHashRoute();

    // Register & Update Service Worker and Evict Stale Caches
    const isLocalDev = Boolean(
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.port === '3000' ||
        window.location.port === '8080'
    );

    if (isLocalDev && 'serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => {
            for (const reg of regs) reg.unregister();
        });
        if (typeof caches !== 'undefined') {
            caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
        }
    } else if ('serviceWorker' in navigator) {
        const CACHE_VER = 'dkd-v3.8.0';
        if (typeof caches !== 'undefined') {
            caches.keys().then(keys => {
                keys.forEach(key => {
                    if (key !== CACHE_VER) caches.delete(key);
                });
            }).catch(() => {});
        }
        navigator.serviceWorker.register('./sw.js?v=3.8.0').then(reg => {
            reg.update();
        }).catch(err => console.warn(err));
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
} else {
    initApp();
}

window.addEventListener('resize', () => {
    if (state.currentView === 'home-view') {
        updateCarousel();
    }
});

export {
    state,
    dict,
    t,
    applyLanguage,
    initTheme,
    applyTheme,
    toggleTheme,
    renderCarousel,
    renderExploreSections,
    openCategoryBySlug,
    navigateToView,
    switchNav,
    playAudio,
    togglePlay,
    applySearchQuery,
    API
};
